import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Outlet, useLocation } from 'react-router-dom';
import { useThemeMode } from '@/hooks/useThemeMode';
import { api } from '@/lib/api';
import {
  TYPOGRAPHY_SCALE_PREFERENCE_KEY,
  applyTypographyScale,
  normalizeTypographyScale,
  setStoredTypographyScale,
} from '@/lib/typography';
import { Sidebar } from './Sidebar';

const MOBILE_ROUTE_LABELS: Array<{ startsWith: string; label: string }> = [
  { startsWith: '/dialer', label: 'Comms' },
  { startsWith: '/voicemails', label: 'Voicemails' },
  { startsWith: '/contacts', label: 'Contacts' },
  { startsWith: '/agents', label: 'Agents' },
  { startsWith: '/analytics', label: 'Analytics' },
  { startsWith: '/status', label: 'Status' },
  { startsWith: '/billing', label: 'Billing' },
  { startsWith: '/tasks', label: 'Tasks' },
  { startsWith: '/settings', label: 'Settings' },
  { startsWith: '/', label: 'Dashboard' },
];

export function Layout() {
  const location = useLocation();
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const { themeMode, resolvedTheme, setThemeMode } = useThemeMode();
  const profilePreferencesQuery = useQuery({
    queryKey: ['me-preferences'],
    queryFn: async () => {
      const res = await api.getMyPreferences();
      return res.data;
    },
    staleTime: 120_000,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    setIsMobileSidebarOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const typographyPreference = profilePreferencesQuery.data?.find(
      (entry) => entry.key === TYPOGRAPHY_SCALE_PREFERENCE_KEY,
    )?.value;
    if (typographyPreference === undefined) return;

    const normalized = normalizeTypographyScale(typographyPreference);
    applyTypographyScale(normalized);
    setStoredTypographyScale(normalized);
  }, [profilePreferencesQuery.data]);

  const quickToggleTheme = () => {
    if (themeMode === 'auto') {
      setThemeMode(resolvedTheme === 'dark' ? 'light' : 'dark');
      return;
    }
    setThemeMode(themeMode === 'dark' ? 'light' : 'dark');
  };

  const quickThemeIcon =
    themeMode === 'auto'
      ? 'brightness_auto'
      : resolvedTheme === 'dark'
        ? 'dark_mode'
        : 'light_mode';
  const mobileRouteLabel = useMemo(
    () =>
      MOBILE_ROUTE_LABELS.find((entry) => location.pathname.startsWith(entry.startsWith))?.label ??
      'CallHub',
    [location.pathname],
  );

  return (
    <div className="relative min-h-screen overflow-x-clip bg-background">
      <div aria-hidden className="pointer-events-none fixed inset-0 -z-10">
        <div className="absolute -left-28 -top-28 h-80 w-80 rounded-full bg-brand-500/15 blur-3xl sm:h-96 sm:w-96" />
        <div className="absolute -right-24 top-0 h-72 w-72 rounded-full bg-accent-500/10 blur-3xl sm:h-80 sm:w-80" />
      </div>

      {isMobileSidebarOpen ? (
        <button
          type="button"
          onClick={() => setIsMobileSidebarOpen(false)}
          className="fixed inset-0 z-40 bg-foreground/35 lg:hidden"
          aria-label="Close sidebar overlay"
        />
      ) : null}

      <Sidebar
        isMobileOpen={isMobileSidebarOpen}
        onCloseMobile={() => setIsMobileSidebarOpen(false)}
        themeMode={themeMode}
        resolvedTheme={resolvedTheme}
        onThemeModeChange={setThemeMode}
      />

      <div className="flex min-h-screen flex-col lg:ml-72">
        <header className="sticky top-0 z-30 border-b border-border/80 bg-card/85 px-3 py-2.5 backdrop-blur lg:hidden">
          <div className="flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => setIsMobileSidebarOpen(true)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-border bg-background/90 text-foreground transition-colors duration-200 hover:bg-muted"
              aria-label="Open sidebar navigation"
            >
              <span className="material-symbols-outlined text-[18px]">menu</span>
            </button>

            <div className="min-w-0 text-center">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                CallHub
              </p>
              <p className="truncate text-sm font-semibold text-foreground">{mobileRouteLabel}</p>
            </div>

            <button
              type="button"
              onClick={quickToggleTheme}
              className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-border bg-background/90 text-foreground transition-colors duration-200 hover:bg-muted"
              aria-label="Quick toggle theme"
              title="Quick toggle theme"
            >
              <span className="material-symbols-outlined text-[18px]">{quickThemeIcon}</span>
            </button>
          </div>
        </header>

        <main className="flex-1 px-2 pb-3 pt-2 sm:px-3 sm:pt-3 lg:px-6 lg:pb-6 lg:pt-5">
          <div className="w-full">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
